import { HelpTooltip } from '@/components/HelpTooltip'
import { formatRelativeSoon } from '@/lib/account-dates'
import { cn } from '@/lib/utils'

// The operator's share of the PMS sign-in window, made visible.
//
// Eviivo allows a fixed number of auth calls per hour per connection (limit, currently 6).
// A slice of that (limit - operatorAllowance, currently 2) is RESERVED for the automatic
// recovery probe that repairs a suspended connection on its own. Before this meter existed,
// an operator diagnosing a broken connection could spend the whole window on "Test
// connection" clicks and silently starve the very job that would have fixed it — so the
// reserve is shown, not hidden, and it is explained in the operator's own terms.
//
// authBudget === null means the METER ITSELF is unavailable (Redis down). The backend fails
// OPEN in exactly that case, so this component renders nothing and callers must not block a
// single action: an unreadable meter is not evidence of an exhausted one.
export interface AuthBudget {
  used: number
  limit: number
  operatorAllowance: number
  operatorRemaining: number
  resetsAt: string | null
}

/** True only when we can actually read the meter AND it says zero. Null budget ⟹ never blocks. */
export function budgetExhausted(budget: AuthBudget | null | undefined): boolean {
  return !!budget && budget.operatorRemaining <= 0
}

/**
 * The client-side twin of EviivoCredentialProbe#operator_auth_budget_message — same numbers,
 * same sentence, so the operator reads one story whether we pre-empt the click or the server
 * refuses it.
 */
export function budgetExhaustedMessage(budget: AuthBudget): string {
  const reserve = Math.max(budget.limit - budget.operatorAllowance, 0)
  return (
    `You've used all ${budget.operatorAllowance} connection checks available this hour. ` +
    `The remaining ${reserve} are reserved for the automatic recovery check that repairs ` +
    `connections on its own. Please try again ${formatRelativeSoon(budget.resetsAt)}.`
  )
}

interface ConnectionCheckMeterProps {
  budget: AuthBudget | null | undefined
  className?: string
}

export default function ConnectionCheckMeter({ budget, className }: ConnectionCheckMeterProps) {
  // No meter reading — say nothing rather than guess. Never a blocked action.
  if (!budget) return null

  const reserve = Math.max(budget.limit - budget.operatorAllowance, 0)
  const remaining = Math.max(budget.operatorRemaining, 0)
  const exhausted = remaining === 0
  const label = exhausted
    ? 'No checks left this hour'
    : `${remaining} of ${budget.operatorAllowance} check${budget.operatorAllowance === 1 ? '' : 's'} left this hour`

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Pips: yours (spent → muted), then the reserved slice behind a divider. */}
      <span className="flex items-center gap-1" aria-hidden="true">
        {Array.from({ length: budget.operatorAllowance }).map((_, i) => (
          <span
            key={`mine-${i}`}
            className={cn('h-1.5 w-4 rounded-full transition-colors', i < remaining ? 'bg-primary' : 'bg-border')}
          />
        ))}
        {reserve > 0 && (
          <>
            <span className="mx-0.5 h-3 w-px bg-border" />
            {Array.from({ length: reserve }).map((_, i) => (
              <span key={`held-${i}`} className="h-1.5 w-4 rounded-full bg-slate-200" />
            ))}
          </>
        )}
      </span>

      <span className={cn('text-[11px] tabular-nums', exhausted ? 'font-medium text-amber-600' : 'text-muted-foreground')}>
        {label}
        {budget.resetsAt && <span className="text-muted-foreground"> · resets {formatRelativeSoon(budget.resetsAt)}</span>}
      </span>

      <HelpTooltip
        content={
          `Your PMS allows ${budget.limit} connection checks an hour. We hold ${reserve} back for the ` +
          `automatic recovery check that repairs connections on its own, so ${budget.operatorAllowance} are yours to spend.`
        }
      />
    </div>
  )
}
